import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Save, 
  Plus, 
  Trash2, 
  Upload, 
  Clock, 
  Eye, 
  X, 
  Edit3,
  Image as ImageIcon
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra
  };
};

const labelMap = {
  kicker: 'Clubs Section Tag / Kicker',
  title: 'Clubs Main Heading',
  description: 'Clubs Overview Description'
};

export default function StudentLifeClubsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    clubs: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });

  const [pendingClubs, setPendingClubs] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [editingClubIndex, setEditingClubIndex] = useState(null);
  const [isAddingClub, setIsAddingClub] = useState(false);
  const [modalClubData, setModalClubData] = useState(null);
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [highlightsText, setHighlightsText] = useState('');

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' })
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeClubsForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str || str === 'null' || str === 'undefined') return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) {
      const mapped = mediaMap[str];
      if (mapped.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${mapped}` : mapped;
      }
      return mapped;
    }

    if (mediaMap[mediaId]) {
      const mapped = mediaMap[mediaId];
      if (mapped.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${mapped}` : mapped;
      }
      return mapped;
    }

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let clubsSection = null;

      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections`,
        { 
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          clubsSection = result.data.find(s => s.section_type === 'student-life-clubs');
        }
      }

      if (!clubsSection) {
        const publicResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: getAuthHeaders()
          }
        );
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            clubsSection = result.data.find(s => s.section_type === 'student-life-clubs');
          }
        }
      }

      if (!clubsSection) {
        throw new Error('Clubs section not found');
      }

      setSectionId(clubsSection.id);

      const content = clubsSection.content_published || clubsSection.content || {};
      const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
      const enData = parsedContent.en || parsedContent;

      let normalizedClubs = [];
      if (Array.isArray(enData.clubs)) {
        normalizedClubs = enData.clubs.map((c, idx) => ({
          title: c.title || c.name || `Club ${idx + 1}`,
          subtitle: c.subtitle || '',
          category: c.category || '',
          description: c.description || '',
          highlights: Array.isArray(c.highlights) ? c.highlights : [],
          images: Array.isArray(c.images) ? c.images : (c.image ? [c.image] : [])
        }));
      }

      const initialData = {
        kicker: enData.kicker || 'Clubs & Activities',
        title: enData.title || 'Explore activities that build confidence, creativity, and friendship',
        description: enData.description || 'From athletics to arts and cultural exchange, student clubs provide vibrant spaces for young people to discover passions, build character, and forge lasting bonds.',
        clubs: normalizedClubs
      };

      const pendingFieldsMap = { kicker: false, title: false, description: false };
      const pendingClubsMap = {};

      const pendingRes = await fetch(`${apiBase}/cms/section/${clubsSection.id}/with-pending`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        if (pendingData.success && pendingData.data) {
          const pendingChanges = pendingData.data.pending_changes || [];
          
          if (Array.isArray(pendingChanges)) {
            pendingChanges.forEach((change) => {
              if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                let val = change.new_value;
                if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'description') {
                  pendingFieldsMap[change.field_id] = true;
                  if (val !== undefined && val !== null) {
                    initialData[change.field_id] = val;
                  }
                } else if (change.field_id && change.field_id.includes('clubs.')) {
                  const match = change.field_id.match(/clubs\.(\d+)(?:\.(add|update|delete))?/);
                  if (match) {
                    const clubIdx = parseInt(match[1], 10);
                    const op = match[2] || 'update';
                    if (op === 'delete') {
                      pendingClubsMap[`club_${clubIdx}_delete`] = true;
                    } else if (op === 'add') {
                      pendingClubsMap[`club_${clubIdx}_add`] = true;
                      if (val && !initialData.clubs[clubIdx]) {
                        initialData.clubs.push(val);
                      }
                    } else {
                      pendingClubsMap[`club_${clubIdx}`] = true;
                      if (val && initialData.clubs[clubIdx]) {
                        initialData.clubs[clubIdx] = val;
                      }
                    }
                  }
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingFieldsMap);
      setPendingClubs(pendingClubsMap);
    } catch (err) {
      console.error('[StudentLifeClubsForm] Load error:', err);
      toast.error('Error loading clubs section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleAddClub = () => {
    const newClub = {
      title: '',
      subtitle: '',
      category: '',
      description: '',
      highlights: [],
      images: []
    };
    setIsAddingClub(true);
    setEditingClubIndex(formData.clubs.length);
    setHighlightsText('');
    setModalClubData(newClub);
  };

  const handleOpenClubModal = (index) => {
    setIsAddingClub(false);
    setEditingClubIndex(index);
    const target = formData.clubs[index] || {};
    const hl = Array.isArray(target.highlights) 
      ? target.highlights.join('\n') 
      : (target.highlights || '');
    setHighlightsText(hl);
    setModalClubData({
      ...target,
      category: target.category || '',
      highlights: Array.isArray(target.highlights) ? [...target.highlights] : [],
      images: Array.isArray(target.images) ? [...target.images] : (target.image ? [target.image] : [])
    });
  };

  const handleCloseClubModal = () => {
    setEditingClubIndex(null);
    setModalClubData(null);
    setHighlightsText('');
    setIsAddingClub(false);
  };

  const handleDeleteClub = async (index) => {
    if (pendingClubs[`club_${index}`] || pendingClubs[`club_${index}_delete`] || pendingClubs[`club_${index}_add`]) {
      toast.info('This club has a pending approval');
      return;
    }

    const clubToDelete = formData.clubs[index];
    if (!window.confirm(`Delete club "${clubToDelete.title || `Club ${index + 1}`}"? This request will be submitted to Principal for approval.`)) return;

    const clubKey = `club_${index}`;
    setSubmitting(prev => ({ ...prev, [clubKey]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `clubs.${index}.delete`,
          field_name: `Delete Club: ${clubToDelete.title || `Club ${index + 1}`}`,
          old_value: clubToDelete || {},
          new_value: null,
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`Deletion request for "${clubToDelete.title || `Club ${index + 1}`}" submitted for Principal approval`);
        setPendingClubs(prev => ({
          ...prev,
          [`club_${index}_delete`]: true
        }));
      } else {
        toast.error(result.error || result.message || 'Failed to submit club deletion');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [clubKey]: false }));
    }
  };

  const handleImageUpload = (clubIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, WebP)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageUploading(prev => ({ ...prev, [clubIndex]: true }));
    setImageProgress(prev => ({ ...prev, [clubIndex]: 0 }));

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('section_type', 'student-life-clubs');

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setImageProgress(prev => ({ ...prev, [clubIndex]: percent }));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const response = JSON.parse(xhr.responseText);
          if (response.success || response.ok) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '');
            const returnedUrl = response.url || response.file_path || (response.filename ? `/api/uploads/studentlife/${response.filename}` : '');
            const fullUrl = returnedUrl.startsWith('http') ? returnedUrl : (returnedUrl ? `${origin}${returnedUrl}` : '');
            const finalUrl = fullUrl || returnedUrl || String(response.media_id || response.id || '');

            setModalClubData(prev => {
              const currentImgs = Array.isArray(prev.images) ? [...prev.images] : [];
              return {
                ...prev,
                images: [...currentImgs, finalUrl],
                image: currentImgs[0] || finalUrl
              };
            });

            const mediaId = response.media_id || response.id;
            if (mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: finalUrl,
                [String(mediaId)]: finalUrl
              }));
            }

            toast.success('Club photo uploaded successfully');
          } else {
            toast.error(response.error || 'Upload failed');
          }
        } catch (pe) {
          toast.error('Invalid server response');
        }
      } else {
        toast.error(`Upload failed (HTTP ${xhr.status})`);
      }
      setImageUploading(prev => ({ ...prev, [clubIndex]: false }));
    });

    xhr.addEventListener('error', () => {
      toast.error('Network error during upload');
      setImageUploading(prev => ({ ...prev, [clubIndex]: false }));
    });

    xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
    xhr.withCredentials = true;
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(uploadFormData);
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: labelMap[fieldName] || fieldName,
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${labelMap[fieldName] || fieldName} proposed for Principal approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitClub = async (clubIndex) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const cleanHighlights = (highlightsText || '')
      .split('\n')
      .map(h => h.trim())
      .filter(Boolean);

    const club = {
      ...modalClubData,
      category: (modalClubData.category || '').trim(),
      highlights: cleanHighlights
    };

    if (!club.title || !club.title.trim()) {
      toast.warning('Please enter a club title');
      return;
    }

    const clubKey = `club_${clubIndex}`;
    setSubmitting(prev => ({ ...prev, [clubKey]: true }));

    const fieldId = isAddingClub ? `clubs.${clubIndex}.add` : `clubs.${clubIndex}.update`;
    const fieldName = isAddingClub ? `Add Club: ${club.title}` : `Update Club: ${club.title}`;
    const oldValue = isAddingClub ? null : (originalData.clubs[clubIndex] || {});

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: club,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${fieldName} submitted for Principal approval`);
        
        if (isAddingClub) {
          setFormData(prev => ({ ...prev, clubs: [...prev.clubs, club] }));
          setPendingClubs(prev => ({ ...prev, [`club_${clubIndex}_add`]: true }));
        } else {
          setFormData(prev => ({
            ...prev,
            clubs: prev.clubs.map((c, i) => i === clubIndex ? club : c)
          }));
          setPendingClubs(prev => ({ ...prev, [clubKey]: true }));
        }

        setEditingClubIndex(null);
        setModalClubData(null);
        setHighlightsText('');
        setIsAddingClub(false);
      } else {
        if (response.status === 409) {
          toast.error('This club already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [clubKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Clubs section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Users size={22} />
        </div>
        <div>
          <h2 className="unified-title">Clubs & Activities</h2>
          <p className="unified-subtitle">
            Manage student clubs, extracurricular activities, descriptions, and gallery photos
          </p>
        </div>
      </div>

      {/* Field 1: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={80}
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g. Clubs & ECA"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 2: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Heading</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength={120}
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Explore activities that build confidence, creativity, and friendship"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 3: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Overview Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength={600}
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="From athletics to arts and cultural exchange, student clubs provide vibrant spaces..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 4: Clubs List */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Student Clubs & Programs ({formData.clubs.length})</label>
            <p className="unified-field-desc">
              Sports, arts, performance, leadership, language and interest-based clubs
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddClub}
          >
            <Plus size={14} /> Add Club
          </button>
        </div>

        {formData.clubs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <Users size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No clubs configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Club" above to create clubs</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.clubs.map((club, idx) => {
              const isPendingDelete = pendingClubs[`club_${idx}_delete`];
              const isPendingAdd = pendingClubs[`club_${idx}_add`];
              const isPendingUpdate = pendingClubs[`club_${idx}`];
              const isPending = isPendingDelete || isPendingAdd || isPendingUpdate;
              const clubImg = (Array.isArray(club.images) && club.images[0]) || club.image;
              const clubImgUrl = getMediaUrl(clubImg);

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 18px',
                    background: isPendingDelete ? '#fef2f2' : isPending ? '#fffdfa' : '#f8fafc',
                    borderRadius: '12px',
                    border: isPendingDelete 
                      ? '2px solid #ef4444' 
                      : isPendingAdd 
                        ? '2px solid #10b981' 
                        : isPendingUpdate 
                          ? '2px solid #f59e0b' 
                          : '1px solid #e2e8f0',
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.8 : 1
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    style={{
                      width: '100px',
                      height: '75px',
                      borderRadius: '8px',
                      background: '#0f172a',
                      overflow: 'hidden',
                      flexShrink: 0,
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    {clubImgUrl ? (
                      <img
                        src={clubImgUrl}
                        alt={club.title || `Club ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <ImageIcon size={22} color="#94a3b8" />
                    )}
                    {clubImgUrl && (
                      <button
                        type="button"
                        onClick={() => setLightboxImage(clubImgUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '3px',
                          right: '3px',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: 'none',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Preview full photo"
                      >
                        <Eye size={10} />
                      </button>
                    )}
                  </div>

                  {/* Club Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '800', fontSize: '15px', color: '#0f172a' }}>
                        {club.title || `Club #${idx + 1}`}
                      </span>
                      {isPendingDelete ? (
                        <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5', padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Deletion
                        </span>
                      ) : isPendingAdd ? (
                        <span className="unified-pending-pill" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0', padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Creation
                        </span>
                      ) : isPendingUpdate ? (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      ) : null}
                    </div>
                    <p style={{ margin: '0 0 3px', fontSize: '13px', color: '#64748b' }}>
                      {club.subtitle || 'No subtitle'}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>
                      {(club.highlights || []).length} highlights &bull; {(club.images || []).length || (club.image ? 1 : 0)} photos
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenClubModal(idx)}
                      disabled={submitting[`club_${idx}`] || isPendingDelete}
                    >
                      <Edit3 size={13} /> Edit
                    </button>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                      onClick={() => handleDeleteClub(idx)}
                      disabled={submitting[`club_${idx}`] || isPending || formData.clubs.length <= 1}
                      title="Delete Club"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit/Add Club Modal */}
      {editingClubIndex !== null && modalClubData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseClubModal}
        >
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                {isAddingClub ? 'Add New Club' : `Edit Club: ${modalClubData.title || `#${editingClubIndex + 1}`}`}
              </h3>
              <button
                type="button"
                onClick={handleCloseClubModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Club Title *</label>
                  <input
                    type="text"
                    value={modalClubData.title || ''}
                    onChange={(e) => setModalClubData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Sports & Athletics Club"
                    className="unified-input"
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Subtitle</label>
                  <input
                    type="text"
                    value={modalClubData.subtitle || ''}
                    onChange={(e) => setModalClubData(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="e.g. Teamwork, discipline, and healthy competition"
                    className="unified-input"
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>
                    Category Badge (optional)
                  </label>
                  <input
                    type="text"
                    value={modalClubData.category || ''}
                    onChange={(e) => setModalClubData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g. Creative Arts & Music, Athletics & Fitness, Performing Arts"
                    className="unified-input"
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Label kategori / badge klub (kosongkan jika ingin auto-detect dari judul: Dancing/Music/Art -&gt; Creative Arts &amp; Music)
                  </span>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Description</label>
                  <textarea
                    value={modalClubData.description || ''}
                    onChange={(e) => setModalClubData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Full description of club activities and skills learned..."
                    className="unified-textarea"
                    rows={3}
                  />
                </div>

                {/* Highlights */}
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Highlights (one per line)</label>
                  <textarea
                    value={highlightsText}
                    onChange={(e) => setHighlightsText(e.target.value)}
                    placeholder={"Weekly training sessions\nInterschool friendly matches\nFitness drills"}
                    className="unified-textarea"
                    rows={4}
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Tekan Enter untuk membuat baris / poin highlight baru
                  </span>
                </div>

                {/* Club Photos */}
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Club Photos</label>
                  
                  {/* Photo List */}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {(Array.isArray(modalClubData.images) ? modalClubData.images : (modalClubData.image ? [modalClubData.image] : [])).map((imgId, iIdx) => {
                      const u = getMediaUrl(imgId);
                      return (
                        <div key={iIdx} style={{ position: 'relative', width: '80px', height: '60px', borderRadius: '8px', overflow: 'hidden', background: '#0f172a' }}>
                          <img src={u} alt="Club" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <button
                            type="button"
                            onClick={() => {
                              const updatedImages = (modalClubData.images || []).filter((_, idx) => idx !== iIdx);
                              setModalClubData(prev => ({ ...prev, images: updatedImages, image: updatedImages[0] || '' }));
                            }}
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              background: 'rgba(239, 68, 68, 0.85)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px'
                            }}
                          >
                            <X size={10} />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <label className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                    <Upload size={14} />
                    <span>
                      {imageUploading[editingClubIndex] 
                        ? `Uploading (${imageProgress[editingClubIndex] || 0}%)...` 
                        : 'Upload Club Photo'}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => handleImageUpload(editingClubIndex, e)}
                      disabled={imageUploading[editingClubIndex]}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseClubModal}
                disabled={submitting[`club_${editingClubIndex}`]}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitClub(editingClubIndex)}
                disabled={submitting[`club_${editingClubIndex}`] || !modalClubData.title || !modalClubData.title.trim() || imageUploading[editingClubIndex]}
              >
                <Save size={14} />
                {submitting[`club_${editingClubIndex}`] ? 'Submitting...' : 'Save & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                zIndex: 10
              }}
            >
              <X size={16} />
            </button>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', display: 'block' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
